import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { F as FOCUS_TYPE } from './common.js';
import { j as transformToVersionedTag } from './utils.js';
import { d as defineCustomElement$7 } from './wpp-icon-error2.js';
import { d as defineCustomElement$6 } from './wpp-icon-warning2.js';
import { d as defineCustomElement$5 } from './wpp-internal-label2.js';
import { d as defineCustomElement$4 } from './wpp-internal-tooltip2.js';
import { d as defineCustomElement$3 } from './wpp-label2.js';
import { d as defineCustomElement$2 } from './wpp-tooltip2.js';
import { d as defineCustomElement$1 } from './wpp-typography2.js';

const wppRadioCss = ":host{--radio-size:var(--wpp-radio-size, 20px);--radio-inside-circle-size:var(--wpp-radio-inside-circle-size, 8px);--radio-inside-circle-bg-color:var(--wpp-radio-inside-circle-bg-color, var(--wpp-grey-color-000));--radio-bg-color:var(--wpp-radio-bg-color, transparent);--radio-bg-color-hover:var(--wpp-radio-bg-color-hover, var(--wpp-grey-color-200));--radio-bg-color-active:var(--wpp-radio-bg-color-active, var(--wpp-grey-color-300));--radio-bg-color-checked:var(--wpp-radio-bg-color-checked, var(--wpp-brand-color));--radio-bg-color-disabled:var(--wpp-radio-bg-color-disabled, transparent);--radio-bg-color-checked-disabled:var(--wpp-radio-bg-color-checked-disabled, var(--wpp-primary-color-300));--radio-border-color:var(--wpp-radio-border-color, var(--wpp-grey-color-500));--radio-border-color-hover:var(--wpp-radio-border-color-hover, var(--wpp-grey-color-700));--radio-border-color-active:var(--wpp-radio-border-color-active, var(--wpp-grey-color-800));--radio-border-color-checked:var(--wpp-radio-border-color-checked, var(--wpp-brand-color));--radio-border-color-disabled:var(--wpp-radio-border-color-disabled, var(--wpp-grey-color-400));--radio-border-color-checked-disabled:var(--wpp-radio-border-color-checked-disabled, var(--wpp-primary-color-300));--radio-first-border-color-focus:var(--wpp-radio-first-border-color-focus, var(--wpp-grey-color-000));--radio-second-border-color-focus:var(--wpp-radio-second-border-color-focus, var(--wpp-brand-color));--radio-label-margin:var(--wpp-radio-label-margin, 0 0 0 8px);--radio-label-text-color-disabled:var(--wpp-radio-label-text-color-disabled, var(--wpp-text-color-disabled));--radio-label-text-color-checked-disabled:var(--wpp-radio-label-text-color-disabled, var(--wpp-text-color-disabled));--radio-border-width:var(--wpp-radio-border-width, var(--wpp-border-width-s));--radio-border-style:var(--wpp-radio-border-style, solid);display:-ms-inline-flexbox;display:inline-flex;outline:none}:host(.radio-wrapper){position:relative;display:-ms-inline-flexbox;display:inline-flex;height:var(--radio-size);cursor:pointer}:host(.radio-wrapper) .label{display:-ms-inline-flexbox;display:inline-flex}:host(.radio-wrapper) .label.with-text .internal-label-wrapper{margin:var(--radio-label-margin)}:host(.radio-wrapper) .label.tab-focus .circle{border-radius:\"\";outline:none;-webkit-box-shadow:0 0 0 1px var(--radio-first-border-color-focus), 0 0 0 2px var(--radio-second-border-color-focus);box-shadow:0 0 0 1px var(--radio-first-border-color-focus), 0 0 0 2px var(--radio-second-border-color-focus);background-color:var(--radio-bg-color-hover);border-color:var(--radio-border-color-hover)}:host(.radio-wrapper) .label .radio-input{position:absolute;cursor:pointer;opacity:0}:host(.radio-wrapper) .label .circle{position:relative;display:inline-block;-webkit-box-sizing:border-box;box-sizing:border-box;width:var(--radio-size);height:var(--radio-size);background-color:var(--radio-bg-color);border:var(--radio-border-width) var(--radio-border-style) var(--radio-border-color);border-radius:var(--wpp-border-radius-round)}:host(.radio-wrapper) .label .circle::before{position:absolute;top:50%;left:50%;display:none;width:var(--radio-inside-circle-size);height:var(--radio-inside-circle-size);background:var(--radio-inside-circle-bg-color);border-radius:var(--wpp-border-radius-round);-webkit-transform:translate(-50%, -50%);transform:translate(-50%, -50%);content:\"\"}:host(.radio-wrapper) .label:hover .circle,:host(.radio-wrapper) .label.hover .circle{background-color:var(--radio-bg-color-hover);border-color:var(--radio-border-color-hover)}:host(.radio-wrapper) .label:active .circle,:host(.radio-wrapper) .label.active .circle{background-color:var(--radio-bg-color-active);border-color:var(--radio-border-color-active)}:host(.radio-wrapper):host(.disabled){cursor:not-allowed}:host(.radio-wrapper):host(.disabled) .label .wpp-internal-label{--label-text-color-disabled:var(--radio-label-text-color-disabled)}:host(.radio-wrapper):host(.disabled) .label .circle{background-color:var(--radio-bg-color-disabled);border-color:var(--radio-border-color-disabled)}:host(.radio-wrapper):host(.checked) .label .circle{background-color:var(--radio-bg-color-checked);border-color:var(--radio-border-color-checked)}:host(.radio-wrapper):host(.checked) .label .circle::before{display:block}:host(.radio-wrapper):host(.checked) .label.tab-focus .circle{border-radius:\"\";outline:none;-webkit-box-shadow:0 0 0 1px var(--radio-first-border-color-focus), 0 0 0 2px var(--radio-second-border-color-focus);box-shadow:0 0 0 1px var(--radio-first-border-color-focus), 0 0 0 2px var(--radio-second-border-color-focus)}:host(.radio-wrapper):host(.checked):host(.disabled) .label .wpp-internal-label{--label-text-color-disabled:var(--radio-label-text-color-checked-disabled)}:host(.radio-wrapper):host(.checked):host(.disabled) .label .circle{background-color:var(--radio-bg-color-checked-disabled);border-color:var(--radio-border-color-checked-disabled)}";

const WppRadio = /*@__PURE__*/ proxyCustomElement(class WppRadio extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.wppChange = createEvent(this, "wppChange", 1);
    this.wppFocus = createEvent(this, "wppFocus", 1);
    this.wppBlur = createEvent(this, "wppBlur", 1);
    this.wppClickRadio = createEvent(this, "wppClickRadio", 1);
    this.onClick = () => {
      if (this.disabled)
        return;
      this.checked = true;
      this.wppChange.emit({
        value: this.value,
        checked: this.checked,
        name: this.name,
      });
      this.wppClickRadio.emit({
        value: this.value,
        checked: this.checked,
      });
    };
    this.onInput = () => {
      if (this.disabled)
        return;
      this.setFocus();
    };
    this.onFocus = (event) => {
      this.wppFocus.emit(event);
    };
    this.onBlur = (event) => {
      this.focusType = FOCUS_TYPE.NONE;
      this.wppBlur.emit(event);
    };
    this.onMouseDown = () => {
      this.focusType = FOCUS_TYPE.MOUSE;
    };
    this.onKeyUp = (event) => {
      if (event.key === 'Tab')
        this.focusType = FOCUS_TYPE.TAB;
    };
    this.hostCssClasses = () => ({
      'wpp-radio': true,
      'radio-wrapper': true,
      disabled: this.disabled,
      checked: this.checked,
    });
    this.labelCssClasses = () => ({
      label: true,
      'tab-focus': this.focusType === FOCUS_TYPE.TAB,
      'with-text': !!this.labelConfig?.text,
      [this.internalState]: true,
    });
    this.focusType = undefined;
    this.name = undefined;
    this.value = undefined;
    this.checked = false;
    this.required = false;
    this.disabled = false;
    this.autoFocus = false;
    this.size = 'm';
    this.ariaProps = {};
    this.labelConfig = undefined;
    this.labelTooltipConfig = {
      popperOptions: { strategy: 'fixed' },
    };
    this.internalState = '';
    this.index = 0;
  }
  /**
   * Method that sets focus on the native input.
   */
  async setFocus() {
    this.inputRef?.focus();
  }
  componentWillLoad() {
    const radioGroup = this.host.closest(transformToVersionedTag('wpp-radio-group'));
    if (radioGroup) {
      this.checked = this.value === radioGroup.value;
    }
  }
  render() {
    return (h(Host, { class: this.hostCssClasses(), "aria-disabled": this.disabled, "aria-checked": this.checked, "aria-required": this.required, onClick: this.onClick, onFocus: this.onFocus, onBlur: this.onBlur, onMouseDown: this.onMouseDown, onKeyUp: this.onKeyUp, tabIndex: this.disabled ? -1 : this.index, exportparts: "label, content, inner" }, h("wpp-label-v2-21-0", { class: this.labelCssClasses(), typography: "s-body", htmlFor: this.name, disabled: this.disabled, optional: !this.required, config: this.labelConfig, tooltipConfig: this.labelTooltipConfig, part: "label" }, h("input", { type: "radio", name: this.name, id: this.name, value: this.value, disabled: this.disabled, checked: this.checked, required: this.required, onInput: this.onInput, autoFocus: this.autoFocus, ref: inputRef => (this.inputRef = inputRef), "aria-label": this.ariaProps.label, class: "radio-input", tabIndex: -1, part: "input", title: "" }), h("div", { class: "circle", part: "circle" }))));
  }
  static get registryIs() { return "wpp-radio-v2-21-0"; }
  get host() { return this; }
  static get style() { return wppRadioCss; }
}, [1, "wpp-radio", "wpp-radio-v2-21-0", {
    "name": [1],
    "value": [1032],
    "checked": [1540],
    "required": [516],
    "disabled": [516],
    "autoFocus": [4, "auto-focus"],
    "size": [1],
    "ariaProps": [16],
    "labelConfig": [1040],
    "labelTooltipConfig": [16],
    "internalState": [1, "internal-state"],
    "index": [2],
    "focusType": [32],
    "setFocus": [64]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["wpp-radio-v2-21-0", "wpp-icon-error-v2-21-0", "wpp-icon-warning-v2-21-0", "wpp-internal-label-v2-21-0", "wpp-internal-tooltip-v2-21-0", "wpp-label-v2-21-0", "wpp-tooltip-v2-21-0", "wpp-typography-v2-21-0"];
  components.forEach(tagName => { switch (tagName) {
    case "wpp-radio-v2-21-0":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, WppRadio);
      }
      break;
    case "wpp-icon-error-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$7();
      }
      break;
    case "wpp-icon-warning-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$6();
      }
      break;
    case "wpp-internal-label-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$5();
      }
      break;
    case "wpp-internal-tooltip-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$4();
      }
      break;
    case "wpp-label-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$3();
      }
      break;
    case "wpp-tooltip-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
    case "wpp-typography-v2-21-0":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { WppRadio as W, defineCustomElement as d };
